import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { select } from '@morningstar/shared/operators';
import { combineLatest, merge, Subject } from 'rxjs';
import { filter, map, tap, withLatestFrom } from 'rxjs/operators';
import { SystemParameters } from '../system-parameters/system-parameters';
import { PeriodContext } from './period-contexts';
import { PeriodService } from './period.service';
import { PeriodsSelectors } from './periods.selectors';

@Component({
  selector: 'wsg-periods',
  templateUrl: './periods.component.html',
  styleUrls: ['./periods.component.css']
})
export class PeriodsComponent implements OnInit, OnDestroy {

  // == CONSTANTS =========================================================

  @Input() label = 'Period';
  @Input() formPropertyName = 'period';
  @Input() value = '';
  @Input() context = PeriodContext.generic;
  @Input() disabled: boolean;
  @Input() forceIncludeCurrentPeriod: boolean = false;
  @Input() parent: FormGroup;
  @Input() includeFuturePeriods: number = 0;

  @Input()
  set location(location: string) {
    this._location.next(location);
  };

  // == STATE =============================================================

  state$ = this.periodService.state$;
  processing$ = this.periodService.processing$;

  private _location = new Subject<string>();
  location$ = this._location.asObservable();

  // == INTERMEDIATE ======================================================

  defaultPeriods$ = this.state$.pipe(
    select<string[]>(PeriodsSelectors.periods)
  );

  currentPeriod$ = this.state$.pipe(
    select<SystemParameters>(PeriodsSelectors.systemParameters),
    map(parameters => parameters.currentPeriod)
  );

  periods$ = combineLatest([this.defaultPeriods$, this.currentPeriod$]).pipe(
    map(([periods, currentPeriod]) => {
      if (this.forceIncludeCurrentPeriod && !periods.includes(currentPeriod)) {
        periods.push(currentPeriod);
      }

      if (this.includeFuturePeriods > 0 && periods.indexOf(currentPeriod) == periods.length-1) // add future periods
      {
        const maxNumeric = periods.reduce((max, period) => {
          const year = parseInt(period.slice(0, 2), 10);
          const month = parseInt(period.slice(2), 10);
          const value = year * 100 + month;
          return value > max ? value : max;
        }, 0);

        let year = Math.floor(maxNumeric / 100);
        let month = maxNumeric % 100;

        // Add the required number of future periods.
        for (let i = 1; i <= this.includeFuturePeriods; i++) {
          month++;
          if (month > 12) {
            month = 1;
            year++;
          }

          const newPeriod = year.toString().padStart(2, '0') + month.toString().padStart(2, '0');
          periods.push(newPeriod);
        }
      }

      if (periods.includes(currentPeriod))
      {
        this.parent.controls[this.formPropertyName].patchValue(currentPeriod); // select current period
      }

      return periods;
    })
  );

  // == SIDE EFFECTS ======================================================

  updateInvoiceVoids$ = this.location$.pipe(
    filter(location => !!location && this.context === PeriodContext.invoiceVoid),
    tap(location => this.periodService.getInvoiceVoidPeriods(location))
  );

  // == SUBSCRIPTIONS =====================================================

  subscriptions = merge(
    this.updateInvoiceVoids$
  ).subscribe();

  // == LIFE CYCLE ========================================================

  constructor(private periodService: PeriodService) { }

  ngOnInit() {
    this.periodService.getSystemParameters();

    if (this.context === PeriodContext.generic) {
      this.periodService.getPeriods();
    } else if (this.context === PeriodContext.invoice){
      this.periodService.getInvoicePeriods();
    }

    this.subscriptions.add(this.periods$.pipe(
      withLatestFrom(this.currentPeriod$),
      tap(([periods, currentPeriod]) => {
        if (!!this.parent.controls[this.formPropertyName].value) {
          // do nothing
        } else if (this.value === '') {
          if (periods.includes(currentPeriod)) {
            this.parent.controls[this.formPropertyName].patchValue(currentPeriod);
          } else {
            this.parent.controls[this.formPropertyName].patchValue(periods[periods.length - 1]);
          }
        } else if (periods.find(p => p === this.value)) {
          this.parent.controls[this.formPropertyName].patchValue(this.value);
        };
      })
    ).subscribe());
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
